// Module 3.2a — BHA Editor.
//
// Sections:
//   1. Identity (name, BHA #, revision, run #, bit size)
//   2. Status (assembly + operational — independent)
//   3. Operating window
//   4. Components — ordered table, reorder up/down, library picker per row,
//      live distance-from-bit / cumulative-length stack-up strip
//   5. Notes
//
// Reference Library wiring: each component's `refCategory` (driven by the
// chosen `role`) determines which library to pick from. Picking a record
// auto-fills the row's display fields. The picker is suppressed when the
// role has no library (jar / shock_sub / other).

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, AlertTriangle, Plus, Trash2, Library,
  ArrowUp, ArrowDown, Wrench, Trash,
} from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/ReportPrimitives";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import {
  emptyBhaRecord, emptyBhaComponent, computeBhaGeometry, TUBULAR_ROLES,
  NO_PPF_ROLES, weightIsDerived, buoyancyFactorFor,
  validateBhaRecord, COMPONENT_ROLES, roleRefCategory, roleLabel,
  ASSEMBLY_STATUSES, OPERATIONAL_STATUSES,
} from "@/lib/bha/schema";
import {
  createBha, getBha, updateBha, deleteBha,
} from "@/lib/bha/storage";
import { getWellProfile } from "@/lib/well/storage";
import { wellStackMode } from "@/lib/well/schema";
import { useStackMode } from "@/lib/stackModeContext";
import { listByCategory, findById } from "@/lib/reflibs/lookup";
import { fillBlanksFromLibrary, applyAllFromLibrary, libraryConflicts } from "@/lib/reflibs/libraryFill";
import { resolve } from "@/lib/rce/resolver";
import { libraryReturnState, getRestoredDraft, getRestoredAnchor, scrollToRestoredAnchor } from "@/lib/reflibs/returnContext";
import RceEditorStrip from "@/components/rce/RceEditorStrip";
import { nextWellSectionHash } from "@/lib/well/completion";

function NativeSelect({ value, onChange, options, testId, disabled }) {
  return (
    <select value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testId}
            className="input-rs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
const NumIn  = (p) => <TextInput type="number" {...p} />;
// Five-Defect Correction Pass, Defect 4 — a single `type="datetime-local"`
// input has inconsistent native-picker support on mobile (notably older
// Android WebViews / Firefox Mobile fall back to raw text entry, which is
// exactly the "cumbersome to enter manually" symptom reported). Splitting
// into separate `type="date"` + `type="time"` native inputs has near-universal
// mobile picker support and still combines into the SAME stored
// "YYYY-MM-DDTHH:mm" string this field always used — no schema/format change.
const DateIn = ({ value, onChange, testId }) => {
  const [datePart, timePart] = String(value || "").split("T");
  return (
    <div className="flex gap-2">
      <input type="date" value={datePart || ""}
             onChange={(e) => onChange(e.target.value ? `${e.target.value}T${timePart || "00:00"}` : "")}
             data-testid={`${testId}-date`} className="input-rs flex-1" />
      <input type="time" value={timePart || ""} disabled={!datePart}
             onChange={(e) => onChange(`${datePart}T${e.target.value || "00:00"}`)}
             data-testid={`${testId}-time`} className="input-rs flex-1" />
    </div>
  );
};

// Build the picker label for any library record (works across categories).
function _refLabel(rec) {
  if (!rec) return "(missing — record may have been removed)";
  if (rec.category === "bit") {
    const parts = [rec.bitSize ? `${rec.bitSize}"` : null, rec.bitType, rec.model, rec.iadcCode];
    return parts.filter(Boolean).join(" · ");
  }
  if (rec.category === "crossover") {
    const parts = [rec.model, rec.topConnection, rec.bottomConnection];
    return parts.filter(Boolean).join(" · ");
  }
  if (rec.category === "stabilizer")     return [rec.model, rec.outerDiameter && `${rec.outerDiameter}"`, rec.bladeType].filter(Boolean).join(" · ");
  if (rec.category === "mud_motor")      return [rec.model, rec.outerDiameter && `${rec.outerDiameter}"`, rec.lobes].filter(Boolean).join(" · ");
  if (rec.category === "rotary_steerable") return [rec.model, rec.outerDiameter && `${rec.outerDiameter}"`, rec.steeringMode].filter(Boolean).join(" · ");
  if (["mwd_tool", "lwd_tool"].includes(rec.category))
    return [rec.model, rec.outerDiameter && `${rec.outerDiameter}"`].filter(Boolean).join(" · ");
  // Tubulars (drill_pipe / hwdp / drill_collar / casing / tubing)
  return [rec.nominalSize, rec.grade, rec.connection].filter(Boolean).join(" · ") || rec.id;
}

// Mapped library values per category — only the slots we can map cleanly.
function _libraryMapped(r) {
  if (!r) return {};
  if (r.category === "bit") return { outerDiameter: r.bitSize ?? "" };
  if (r.category === "crossover") {
    return {
      outerDiameter: r.outerDiameter ?? "",
      innerDiameter: r.innerDiameter ?? "",
      connection:    [r.topConnection, r.bottomConnection].filter(Boolean).join(" × "),
    };
  }
  if (["drill_pipe", "hwdp", "drill_collar", "casing", "tubing"].includes(r.category)) {
    // T0 — v2 library records store the canonical key `weight` (lb/ft).
    return {
      outerDiameter: r.outerDiameter ?? "",
      innerDiameter: r.innerDiameter ?? "",
      weightPpf:     r.weight ?? "",
      connection:    r.connection ?? "",
    };
  }
  // BHA tools (motor / RSS / MWD / LWD / stabilizer)
  return {
    outerDiameter: r.outerDiameter ?? "",
    innerDiameter: r.innerDiameter ?? "",
    connection:    r.connection ?? "",
  };
}

// Picking a record NEVER overwrites a value the operator already entered —
// it fills blanks only. Clearing the picker unlinks the reference and keeps
// every entered dimension as standalone component data.
function _populateFromLibrary(comp, refId) {
  const prevRef = comp.refId ? findById(comp.refId) : null;
  const prevLabel = prevRef ? _refLabel(prevRef) : "";
  if (!refId) {
    const wasLibraryName = comp.displayName && comp.displayName === prevLabel;
    return { ...comp, refId: "", displayName: wasLibraryName ? "" : comp.displayName };
  }
  const r = findById(refId);
  if (!r) return { ...comp, refId };
  const out = fillBlanksFromLibrary({ ...comp, refId }, _libraryMapped(r));
  if (!comp.displayName || comp.displayName === prevLabel) out.displayName = _refLabel(r);
  return out;
}

// Explicit operator action — replaces populated fields with the linked
// record's values.
function _applyLibraryValues(comp) {
  const r = comp.refId ? findById(comp.refId) : null;
  if (!r) return comp;
  return { ...applyAllFromLibrary(comp, _libraryMapped(r)), displayName: _refLabel(r) };
}

function _componentLibraryConflicts(comp) {
  const r = comp.refId ? findById(comp.refId) : null;
  if (!r) return [];
  return libraryConflicts(comp, _libraryMapped(r));
}

// =============================================================================
// Editor
// =============================================================================
export default function BhaEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { stackMode } = useStackMode();
  const { wellId, bhaId } = useParams();
  const isNew = !bhaId;
  const well = useMemo(() => getWellProfile(wellId), [wellId]);

  const [rec, setRec]       = useState(() => ({ ...emptyBhaRecord(), wellId }));
  const [errors, setErrors] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (isNew) {
      setRec({ ...emptyBhaRecord(), wellId });
      setNotFound(false);
      return;
    }
    const row = getBha(bhaId);
    if (row && row.wellId === wellId) {
      setRec(row);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [bhaId, wellId, isNew]);

  // Reference Library Return-Workflow — restore in-progress edits that
  // survived a round trip to a Reference Library (Manage ___ Library →
  // Add/Edit Record → ← BACK). Consumed once, then cleared from history
  // state so a later refresh never re-applies a stale draft.
  useEffect(() => {
    const draft = getRestoredDraft(location);
    if (!draft) return;
    const matches = isNew ? (!draft.id && draft.wellId === wellId) : (draft.id === bhaId);
    if (matches) setRec(draft);
    scrollToRestoredAnchor(getRestoredAnchor(location));
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mud weight for the buoyancy factor. Resolved through the canonical RCE
  // resolver rather than reading the Mud Program directly, so the BHA sees
  // the SAME value the Kill Sheet does: resolve() applies the established
  // precedence (session override -> latest Daily Mud Report -> active Mud
  // Program) that lib/mud/derivers.js owns. Reading the program alone would
  // ignore a filed DMR and show two different mud weights in one app.
  const mudWeight = useMemo(() => {
    try {
      const r = resolve("mud.weightInPpg", { wellId });
      if (!r || r.confidence === "unknown" || r.value === "" || r.value == null) return null;
      const n = Number(r.value);
      return Number.isFinite(n) ? { ppg: n, label: r.source?.label || "" } : null;
    } catch { return null; }
  }, [wellId]);
  const buoyancyFactor = useMemo(
    () => buoyancyFactorFor(mudWeight?.ppg), [mudWeight],
  );

  const baked = useMemo(
    () => computeBhaGeometry(rec, { buoyancyFactor }),
    [rec, buoyancyFactor],
  );
  const totalLen = (() => {
    const last = baked.components[baked.components.length - 1];
    return last && Number.isFinite(Number(last.cumulativeLengthFt))
      ? Number(last.cumulativeLengthFt) : null;
  })();

  const set = (patch) => setRec((cur) => ({ ...cur, ...patch }));

  // RC-1 · Unsaved Changes Protection (presentation only).
  const unsaved = useUnsavedChanges({ data: rec, enabled: !notFound, onSave: () => handleSave() });

  // ---- Component table mutation helpers ---------------------------------
  const _setComps = (next) => set({ components: next });

  const addComponent = (role = "other") => {
    const next = [...(rec.components || []), { ...emptyBhaComponent(role) }];
    _setComps(next);
  };
  const removeComponent = (idx) => {
    const next = [...(rec.components || [])]; next.splice(idx, 1); _setComps(next);
  };
  const patchComponent = (idx, patch) => {
    const next = [...(rec.components || [])]; next[idx] = { ...next[idx], ...patch }; _setComps(next);
  };
  const move = (idx, dir) => {
    const next = [...(rec.components || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    _setComps(next);
  };
  const changeRole = (idx, newRole) => {
    patchComponent(idx, {
      role: newRole,
      refCategory: roleRefCategory(newRole),
      refId: "",
      displayName: "",
    });
  };

  // ---- Save / Delete ----------------------------------------------------
  const _doSaveBha = () => {
    const baked0 = computeBhaGeometry(rec);
    const v = validateBhaRecord(baked0);
    if (!v.ok) {
      setErrors(v.errors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return null;
    }
    try {
      const saved = isNew ? createBha(rec) : updateBha(bhaId, rec);
      setErrors([]);
      unsaved.markSaved(saved || rec);
      return saved;
    } catch (e) {
      setErrors(e.validationErrors || [e.message || "Save failed"]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return null;
    }
  };
  const handleSave = () => {
    const saved = _doSaveBha();
    if (!saved) return false;
    toast.success("BHA saved successfully");
    // RC-1 Commercial Workflow QA — if the operator arrived from the Well
    // Profile workbook (`?return=well`), return them there and scroll to the
    // next engineering section (Drill String). Otherwise keep the pre-
    // existing per-module editor navigation.
    const returnToWell = new URLSearchParams(window.location.search).get("return") === "well";
    if (returnToWell) {
      navigate(nextWellSectionHash(wellId, "bha"));
    } else if (isNew) {
      // Creating a new BHA is the one case where this URL update genuinely
      // changes pathname (from the "new" route to the saved id), which
      // ScrollToTop would otherwise reset to the top for. Pass the current
      // scroll position through its existing restoreScrollY mechanism so
      // the operator stays where they were instead of losing their place.
      navigate(`/wells/${wellId}/bha/${saved.id}`, { replace: true, state: { restoreScrollY: window.scrollY } });
    }
    // Editing an existing BHA navigates to the same URL it's already on --
    // ScrollToTop already skips its reset when pathname+search are
    // unchanged, so no navigation is needed here at all.
    return true;
  };
  // Five-Defect Correction Pass, Defect 5 — a clear NEXT action once the BHA
  // is actually saved (existing completion rule: `validateBhaRecord` must
  // pass, enforced by `_doSaveBha` above). Reuses the SAME existing route
  // the `?return=well` flow already navigates to — no new route invented.
  const handleSaveAndNext = () => {
    const saved = _doSaveBha();
    if (!saved) return;
    navigate(nextWellSectionHash(wellId, "bha"));
  };
  const handleDelete = () => {
    if (!bhaId) return;
    if (!window.confirm(`Delete BHA "${rec.name}"?\n\nReference libraries are untouched.`)) return;
    deleteBha(bhaId);
    navigate(`/wells/${wellId}/bha`);
  };

  if (!well) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  // Surface/Subsea isolation — see KillSheetOpHub for rationale.
  if (wellStackMode(well) !== stackMode) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        &quot;{well.wellName || "This well"}&quot; belongs to {wellStackMode(well) === "subsea" ? "Subsea" : "Surface"} mode.
        Switch the operating-mode toggle back to view its BHAs, or{" "}
        <Link to="/wells" className="text-amber-400">go back to Wells</Link>.
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        BHA not found for this well. <Link to={`/wells/${wellId}/bha`} className="text-amber-400">Back to BHAs</Link>.
      </div>
    );
  }

  return (
    <div data-testid="bha-editor" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/bha`)}
                data-testid="bha-editor-back"
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> BHAs
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button" onClick={handleDelete}
                    data-testid="bha-editor-delete"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] text-red-300 border border-red-500/30 hover:bg-red-500/10">
              <Trash className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3.2 · {well.wellName || "Well"} · BHA
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)] inline-flex items-center gap-2">
          <Wrench className="w-5 h-5 text-amber-400" />
          {isNew ? "New BHA" : (rec.name || "(unnamed BHA)")}
        </h1>
      </div>

      <RceEditorStrip
        ownerKey="bha"
        ownerLabel="BHA · Engineering Context"
        consumes={["project.operator", "rig.name", "well.name", "well.holeSection", "well.holeSizeIn"]}
      />

      <RceEditorStrip
        ownerKey="bha"
        ownerLabel="BHA · Engineering Context"
        consumes={["project.operator", "rig.name", "well.name", "well.holeSection", "well.holeSizeIn"]}
      />

      {errors.length > 0 && (
        <div data-testid="bha-editor-errors"
             className="card-rs p-3 border-red-500/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <ul className="text-xs text-red-300 list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* 1. Identity ------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="BHA name (required)">
            <TextInput value={rec.name} onChange={(v) => set({ name: v })} testId="bha-name" />
          </Field>
          <Field label="BHA number">
            <TextInput value={rec.bhaNumber} onChange={(v) => set({ bhaNumber: v })} testId="bha-bhaNumber" />
          </Field>
          <Field label="Revision (optional)" helper="A, B, C — for small modifications without a new BHA.">
            <TextInput value={rec.revision} onChange={(v) => set({ revision: v })} testId="bha-revision" />
          </Field>
          <Field label="Run number">
            <TextInput value={rec.runNumber} onChange={(v) => set({ runNumber: v })} testId="bha-runNumber" />
          </Field>
          <Field label="Bit / hole size (in)">
            <NumIn value={rec.bitSize} onChange={(v) => set({ bitSize: v })} testId="bha-bitSize" />
          </Field>
        </div>
      </section>

      {/* 2. Status -------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Status</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Assembly status" helper="Workflow track — independent of operations.">
            <NativeSelect value={rec.assemblyStatus} onChange={(v) => set({ assemblyStatus: v })}
                          options={ASSEMBLY_STATUSES.map(s => ({ value: s, label: s }))}
                          testId="bha-assemblyStatus" />
          </Field>
          <Field label="Operational status" helper="Operational track — independent of approval state.">
            <NativeSelect value={rec.operationalStatus} onChange={(v) => set({ operationalStatus: v })}
                          options={OPERATIONAL_STATUSES.map(s => ({ value: s, label: s }))}
                          testId="bha-operationalStatus" />
          </Field>
        </div>
      </section>

      {/* 3. Operating window ---------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Operating Window</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Start (date / time)">
            <DateIn value={rec.startDateTime} onChange={(v) => set({ startDateTime: v })} testId="bha-startDateTime" />
          </Field>
          <Field label="End (date / time)">
            <DateIn value={rec.endDateTime} onChange={(v) => set({ endDateTime: v })} testId="bha-endDateTime" />
          </Field>
          <Field label="Depth in (ft)">
            <NumIn value={rec.depthInFt} onChange={(v) => set({ depthInFt: v })} testId="bha-depthInFt" />
          </Field>
          <Field label="Depth out (ft)">
            <NumIn value={rec.depthOutFt} onChange={(v) => set({ depthOutFt: v })} testId="bha-depthOutFt" />
          </Field>
          <Field label="Rotating hours">
            <NumIn value={rec.rotatingHours} onChange={(v) => set({ rotatingHours: v })} testId="bha-rotatingHours" />
          </Field>
          <Field label="Total hours">
            <NumIn value={rec.totalHours} onChange={(v) => set({ totalHours: v })} testId="bha-totalHours" />
          </Field>
        </div>
      </section>

      {/* 4. Components ---------------------------------------------------- */}
      <section className="card-rs p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-[13px] font-semibold">Components <span className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] font-normal">(bit at top, surface at bottom)</span></h2>
          <button type="button" onClick={() => addComponent("other")}
                  data-testid="bha-component-add"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
            <Plus className="w-3 h-3" /> Add component
          </button>
        </div>

        {(rec.components?.length || 0) === 0 ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            No components yet. Tap <span className="text-amber-400">Add component</span> to
            start — usually you'll start with the bit at position #1.
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="bha-components">
            {baked.components.map((c, idx) => (
              <ComponentRow key={c.componentUid}
                            idx={idx}
                            comp={c}
                            isFirst={idx === 0}
                            isLast={idx === baked.components.length - 1}
                            onRoleChange={(r) => changeRole(idx, r)}
                            onPatch={(p) => patchComponent(idx, p)}
                            onPickRef={(refId) => patchComponent(idx, _populateFromLibrary(c, refId))}
                            onApplyLibrary={() => patchComponent(idx, _applyLibraryValues(c))}
                            onMoveUp={() => move(idx, -1)}
                            onMoveDown={() => move(idx, +1)}
                            onRemove={() => removeComponent(idx)}
                            libraryState={libraryReturnState(location, rec, `bha-component-${idx}`)} />
            ))}
          </div>
        )}

        {/* Mud weight drives the buoyancy factor. Read-only and resolved,
            never entered here — the Mud Program owns it. */}
        <div data-testid="bha-mud-source"
             className="mt-3 text-[11px] rounded px-3 py-2 flex items-center gap-x-4 gap-y-1 flex-wrap"
             style={{ background: "var(--rs-surface-2,#141619)", color: "var(--rs-text-2,#c2cad4)" }}>
          <span>
            Mud weight:{" "}
            <span className="text-amber-400 font-mono font-semibold" data-testid="bha-mud-weight">
              {mudWeight ? `${mudWeight.ppg} ppg` : "—"}
            </span>
          </span>
          <span>
            Buoyancy factor:{" "}
            <span className="text-amber-400 font-mono font-semibold" data-testid="bha-buoyancy-factor">
              {buoyancyFactor != null ? buoyancyFactor : "—"}
            </span>
          </span>
          <span className="text-[var(--rs-text-3,#6b7785)]">
            {mudWeight
              ? `From: ${mudWeight.label || "active Mud Program"}`
              : "No mud weight available — set up a Mud Program for this well first."}
          </span>
        </div>

        {baked.components.length > 0 && (
          <div data-testid="bha-stackup-totals"
               className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[11px] font-mono text-[var(--rs-text-2,#c2cad4)]">
              Total components: <span className="text-amber-400">{baked.components.length}</span>
            </span>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-[13px] font-mono font-bold uppercase tracking-wide text-amber-400">
                Total BHA Length:{" "}
                <span className="text-[15px]">
                  {totalLen != null ? `${totalLen.toLocaleString(undefined, { maximumFractionDigits: 2 })} ft` : "—"}
                </span>
              </span>
              <span data-testid="bha-total-weight"
                    className="text-[13px] font-mono font-bold uppercase tracking-wide text-amber-400">
                Total BHA Weight:{" "}
                <span className="text-[15px]">
                  {baked.totalBhaWeightLbs !== "" && baked.totalBhaWeightLbs != null
                    ? `${Number(baked.totalBhaWeightLbs).toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs`
                    : "—"}
                </span>
              </span>
              <span data-testid="bha-total-buoyed-weight"
                    className="text-[13px] font-mono font-bold uppercase tracking-wide text-amber-400">
                Total BHA Buoyed Weight:{" "}
                <span className="text-[15px]">
                  {baked.totalBhaBuoyedWeightLbs !== "" && baked.totalBhaBuoyedWeightLbs != null
                    ? `${Number(baked.totalBhaBuoyedWeightLbs).toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs`
                    : "—"}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* RC-1 — bottom-of-list Add button so the user never has to scroll up. */}
        {(rec.components?.length || 0) > 0 && (
          <div className="mt-4 pt-3 border-t border-[var(--rs-border,#1f2630)] flex justify-center">
            <button type="button" onClick={() => addComponent("other")}
                    data-testid="bha-component-add-bottom"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
              <Plus className="w-3.5 h-3.5" /> Add component
            </button>
          </div>
        )}
      </section>

      {/* 5. Notes --------------------------------------------------------- */}
      <section className="card-rs p-4">
        <h2 className="text-[13px] font-semibold mb-3">Notes</h2>
        <Field label="Free-text notes">
          <TextArea value={rec.notes} onChange={(v) => set({ notes: v })} testId="bha-notes" rows={4} />
        </Field>
      </section>

      {/* RC-1 Editor Save Consistency — primary Save button is anchored to
          the bottom of the editor (immediately after the last section) so
          the operator finishes with a clear Save action. */}
      <div data-testid="bha-editor-bottom-bar"
           className="card-rs p-4 flex flex-wrap items-center justify-end gap-2">
        <button type="button"
                onClick={() => unsaved.guardedNavigate(`/wells/${wellId}/bha`)}
                data-testid="bha-editor-back-bottom"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-[var(--rs-text-2)] border border-[var(--rs-border,#1f2630)] hover:bg-[var(--rs-surface-2,#12161d)] order-4 sm:order-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button type="button" onClick={handleSave}
                data-testid="bha-editor-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold order-1"
                style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
          <Save className="w-4 h-4" /> Save BHA
        </button>
        {!isNew && (
          <button type="button" onClick={handleSaveAndNext}
                  data-testid="bha-editor-next"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium order-2"
                  style={{ background: "transparent", color: "var(--rs-amber)", border: "1px solid var(--rs-amber)" }}>
            Next → Drill String
          </button>
        )}
      </div>

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />

      {/* RC-1 — Floating Engineering Toolbar (visible while scrolling). */}
      <FloatingActionToolbar
        testId="bha-fab"
        actions={[
          { id: "add",  icon: Plus, label: "Add component", onClick: () => addComponent("other"), testId: "fab-bha-add" },
          { id: "save", icon: Save, label: "Save",          onClick: handleSave,                  testId: "fab-bha-save", primary: true },
        ]}
      />
    </div>
  );
}

// =============================================================================
// ComponentRow — one row in the components table.
// =============================================================================
function ComponentRow({
  idx, comp, isFirst, isLast,
  onRoleChange, onPatch, onPickRef, onApplyLibrary,
  onMoveUp, onMoveDown, onRemove, libraryState,
}) {
  const refCat = roleRefCategory(comp.role);

  // Why a weight reads "—". Without this the operator cannot tell a
  // deliberately-blank component from one that is silently missing from the
  // BHA weight total.
  const weightHint = (() => {
    const _has = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
    if (weightIsDerived(comp)) return null;          // calculated — nothing missing
    if (_has(comp.totalWeightLbs)) return null;      // entered directly — fine
    const hasLen = _has(comp.lengthFt);
    const hasPpf = _has(comp.weightPpf);
    if (TUBULAR_ROLES.includes(comp.role) && hasLen && !hasPpf)
      return "Weight (ppf) missing — total weight cannot be calculated.";
    if (hasPpf && !hasLen)
      return "Length missing — enter a length to calculate, or type the total weight directly.";
    return "Total weight not entered — this component is excluded from the BHA weight total.";
  })();
  const conflicts = _componentLibraryConflicts(comp);

  // Compute library options for this row (memo per refCat for performance).
  const refOptions = useMemo(() => {
    if (!refCat) return null;
    const all = listByCategory(refCat);
    return [
      { value: "", label: `— Pick from ${refCat} library —` },
      ...all.map((r) => ({ value: r.id, label: _refLabel(r) })),
    ];
  }, [refCat]);

  return (
    <div id={`bha-component-${idx}`}
         data-testid={`bha-component-${idx}`}
         className="rounded-md border border-[var(--rs-border,#1f2630)] p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rs-text-dim,#9aa6b2)]">
          #{idx + 1} · {roleLabel(comp.role)}
          {comp.displayName ? (
            <span className="normal-case tracking-normal text-[var(--rs-text-2,#c2cad4)]">
              {" · "}{comp.displayName}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onMoveUp} disabled={isFirst}
                  data-testid={`bha-component-${idx}-up`}
                  aria-label="Move up"
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 disabled:opacity-30">
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onMoveDown} disabled={isLast}
                  data-testid={`bha-component-${idx}-down`}
                  aria-label="Move down"
                  className="p-1 rounded text-zinc-400 hover:text-amber-400 disabled:opacity-30">
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onRemove}
                  data-testid={`bha-component-${idx}-remove`}
                  aria-label="Remove component"
                  className="p-1 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Role">
          <NativeSelect value={comp.role} onChange={onRoleChange}
                        options={COMPONENT_ROLES.map((r) => ({ value: r.role, label: r.label }))}
                        testId={`bha-component-${idx}-role`} />
        </Field>
        {refOptions && (
          <Field label="Library record"
                 helper="Optional. Fills blank fields only — your entered values are never replaced.">
            <NativeSelect value={comp.refId} onChange={onPickRef}
                          options={refOptions}
                          testId={`bha-component-${idx}-refId`} />
          </Field>
        )}
        {/* Roles with no reference library (Jar, Shock Sub, Other) get their
            name from the operator instead of a library record — otherwise
            displayName is unreachable and the row is only ever identified by
            its generic role label. */}
        {!refCat && (
          <Field label="Component name"
                 helper="Describes this specific tool — there is no reference library for this role.">
            <TextInput value={comp.displayName}
                       onChange={(v) => onPatch({ displayName: v })}
                       testId={`bha-component-${idx}-displayName`} />
          </Field>
        )}
        <Field label="OD (in)">
          <NumIn value={comp.outerDiameter}
                 onChange={(v) => onPatch({ outerDiameter: v })}
                 testId={`bha-component-${idx}-od`} />
        </Field>
        <Field label="ID (in)">
          <NumIn value={comp.innerDiameter}
                 onChange={(v) => onPatch({ innerDiameter: v })}
                 testId={`bha-component-${idx}-id`} />
        </Field>
        <Field label="Length (ft, required for stack-up)">
          <NumIn value={comp.lengthFt}
                 onChange={(v) => onPatch({ lengthFt: v })}
                 testId={`bha-component-${idx}-length`} />
        </Field>
        {["drill_pipe", "hwdp", "drill_collar"].includes(comp.role) && (
          <Field label="Piece count (joints)">
            <NumIn value={comp.pieceCount}
                   onChange={(v) => onPatch({ pieceCount: v })}
                   testId={`bha-component-${idx}-pieces`} />
          </Field>
        )}
        <Field label="Connection">
          <TextInput value={comp.connection}
                     onChange={(v) => onPatch({ connection: v })}
                     testId={`bha-component-${idx}-conn`} />
        </Field>
        {!NO_PPF_ROLES.includes(comp.role) && (
          <Field label="Weight (ppf)"
                 helper="Optional. With a length entered, this calculates the total weight.">
            <NumIn value={comp.weightPpf}
                   onChange={(v) => onPatch({ weightPpf: v })}
                   testId={`bha-component-${idx}-weight`} />
          </Field>
        )}
        {/* Total weight is derived whenever both length and ppf are present,
            and operator-entered otherwise — so a tool with only a stated
            total weight keeps it. */}
        {weightIsDerived(comp) ? (
          <Field label="Total weight (lbs)" helper="Auto-calculated: Length × Weight (ppf)">
            <input type="text" readOnly disabled
                   data-testid={`bha-component-${idx}-totalWeight`}
                   value={comp.totalWeightLbs !== "" && comp.totalWeightLbs != null
                     ? Number(comp.totalWeightLbs).toLocaleString(undefined, { maximumFractionDigits: 2 })
                     : "—"}
                   className="input-rs"
                   style={{ opacity: 0.75, cursor: "default" }} />
          </Field>
        ) : (
          <Field label="Total weight (lbs)">
            <NumIn value={comp.totalWeightLbs}
                   onChange={(v) => onPatch({ totalWeightLbs: v })}
                   testId={`bha-component-${idx}-totalWeight`} />
          </Field>
        )}
        <Field label="Buoyed weight (lbs)" helper="Auto-calculated: Total weight × buoyancy factor">
          <input type="text" readOnly disabled
                 data-testid={`bha-component-${idx}-buoyedWeight`}
                 value={comp.buoyedWeightLbs !== "" && comp.buoyedWeightLbs != null
                   ? Number(comp.buoyedWeightLbs).toLocaleString(undefined, { maximumFractionDigits: 2 })
                   : "—"}
                 className="input-rs"
                 style={{ opacity: 0.75, cursor: "default" }} />
        </Field>
      </div>

      {weightHint && (
        <div data-testid={`bha-component-${idx}-weight-hint`}
             className="mt-2 text-[10.5px] text-amber-300">
          {weightHint}
        </div>
      )}

      <div className="mt-2">
        <Field label="Notes">
          <TextInput value={comp.notes}
                     onChange={(v) => onPatch({ notes: v })}
                     testId={`bha-component-${idx}-notes`} />
        </Field>
      </div>

      {conflicts.length > 0 && (
        <div data-testid={`bha-component-${idx}-libconflict`}
             className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
          <div className="text-[10.5px] text-amber-300">
            Your entered values differ from the linked library record and were kept:{" "}
            {conflicts.map((c) => `${c.label} ${c.current} (library ${c.library})`).join(" · ")}
          </div>
          <button type="button" onClick={onApplyLibrary}
                  data-testid={`bha-component-${idx}-applylib`}
                  className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
            Apply library values
          </button>
        </div>
      )}

      {refCat && (
        <div className="mt-2">
          <Link to={`/reference-libraries/${refCat}`}
                state={libraryState}
                data-testid={`bha-component-${idx}-managelib`}
                className="inline-flex items-center gap-1 text-[10.5px] text-amber-400">
            <Library className="w-3 h-3" /> Manage {roleLabel(comp.role)} library
          </Link>
        </div>
      )}
    </div>
  );
}
